/**
 * POST `pharmacy/mn-judge/assess` — asks the AI service to predict the payer's
 * outcome for an answered PA questionnaire.
 *
 * Pass 1 is fired by the RPA with the agent's answers; the dashboard fires
 * pass 2 after a reviewer edits an answer, so `answer_source` is always
 * `human_reviewed` here. The verdict is advisory: nothing is submitted or
 * denied off the back of it.
 *
 * @module api/postCall/postMnJudgeAssessment
 */
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getAuthHeaders, handleApiResponse } from "../utils/apiHelpers";

/** Which pass produced the answers being judged. */
export const MN_JUDGE_ANSWER_SOURCE = {
  AGENT: "agent",
  HUMAN_REVIEWED: "human_reviewed",
} as const;

/** Attempt number the dashboard (pass 2) records its assessment under. */
export const MN_JUDGE_HUMAN_REVIEWED_ATTEMPT = 2;

/** One answered questionnaire row sent for judging. */
export interface MnJudgeSubmittedAnswer {
  question: string;
  answer: string | string[] | null;
  question_name: string | null;
}

/** Request body for POST `pharmacy/mn-judge/assess`. */
export interface MnJudgeAssessRequest {
  identifier: string;
  answers: MnJudgeSubmittedAnswer[];
  answer_source: string;
  attempt: number;
}

/**
 * Submits the reviewed questionnaire answers for a medical-necessity verdict.
 *
 * @param body - Case identifier, answers, source pass, and attempt number
 * @returns Parsed judge verdict payload
 * @throws Rethrows any non-2xx HTTP error after logging
 */
export const postMnJudgeAssessment = async (
  body: MnJudgeAssessRequest,
): Promise<unknown> => {
  if (!body.identifier) {
    throw new Error("identifier is required to assess a questionnaire");
  }

  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(API_ENDPOINTS.MN_JUDGE_ASSESS, {
      method: "POST",
      headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const result = await handleApiResponse(response);
    logDataToConsole("postMnJudgeAssessment", result);
    return result;
  } catch (error) {
    logError(error as Error, "Error in postMnJudgeAssessment");
    throw error;
  }
};
